/**
 * studyPlanService.ts
 * Motor determinístico de planejamento de estudos personalizados,
 * projeções CEFR, cronograma diário adaptativo e suporte offline-ready.
 */

import { dbManager } from './db_manager';
import { getLocalISOString } from '../utils/timezoneUtils';

export interface StudyPlan {
  id: string;
  user_id: string;
  language_code: string; // 'en', 'fr', 'it'
  current_level: string;  // 'A1', 'A2', 'B1', 'B2', 'C1', 'C2'
  daily_goal_minutes: number; // 5, 10, 15, 30, 60
  current_day_index: number; // 1, 2, 3...
  start_date: string;
  projected_a2_date?: string;
  projected_b1_date?: string;
  projected_b2_date?: string;
  projected_c1_date?: string;
  projected_c2_date?: string;
  total_minutes_studied: number;
}

export type ExerciseTaskType = 
  | 'flashcards_words'
  | 'flashcards_phrases'
  | 'pronunciation'
  | 'listening'
  | 'writing'
  | 'grammar_reading'
  | 'grammar_quiz'
  | 'synced_audiobook'
  | 'active_voice'
  | 'review_errors';

export interface PlanTask {
  id: string;
  type: ExerciseTaskType;
  title: string;
  description: string;
  estimated_minutes: number;
  level: string;
  target_url: string;
  completed: boolean;
  target_count?: number; // Para tarefas baseadas em itens (ex: 15 palavras)
  target_seconds?: number; // Para tarefas baseadas em tempo (ex: 15 min * 60)
  current_progress?: number; // Progresso atual: contagem de itens ou segundos
  is_priority_error?: boolean;
  metadata?: Record<string, any>;
}

export interface DailyPlanProgress {
  plan_day: number;
  schedule_date: string;
  tasks: PlanTask[];
  completed_tasks_count: number;
  total_tasks_count: number;
  minutes_spent: number;
  status: 'pending' | 'in_progress' | 'completed';
}

// Horas de estudo ativo necessárias segundo o CEFR por nível acumulado
export const CEFR_HOURS_REQUIREMENTS: Record<string, number> = {
  'A1': 90,
  'A2': 180,
  'B1': 350,
  'B2': 550,
  'C1': 750,
  'C2': 1000
};

import { getGrammarTopicsForLangAndLevel } from './grammarTopics';

class StudyPlanService {
  private LOCAL_STORAGE_KEY_PREFIX = 'libritui_study_plan_';
  private LOCAL_TASKS_KEY_PREFIX = 'libritui_daily_tasks_';

  /**
   * Calcula projeções matemáticas de conclusão de níveis baseadas no tempo diário
   */
  public calculateProjections(startLevel: string, dailyMinutes: number, startDate: Date = new Date()): {
    a2Date?: string;
    b1Date?: string;
    b2Date?: string;
    c1Date?: string;
    c2Date?: string;
  } {
    const minsPerDay = Math.max(5, dailyMinutes);
    const startLvl = (startLevel || 'A1').toUpperCase();

    const levelsOrder = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
    const startIndex = levelsOrder.indexOf(startLvl);

    const result: Record<string, string> = {};
    let accumulatedHours = startIndex >= 0 ? (CEFR_HOURS_REQUIREMENTS[startLvl] * 0.2) : 0;
    let totalCumulativeDays = 0;

    levelsOrder.forEach((lvl, idx) => {
      if (idx > startIndex) {
        const requiredHours = CEFR_HOURS_REQUIREMENTS[lvl];
        const hoursDelta = Math.max(20, requiredHours - accumulatedHours);
        const daysRequired = Math.ceil((hoursDelta * 60) / minsPerDay);
        totalCumulativeDays += daysRequired;

        const targetDate = new Date(startDate.getTime());
        targetDate.setDate(targetDate.getDate() + totalCumulativeDays);
        const iso = targetDate.toISOString().split('T')[0];

        if (lvl === 'A2') result.a2Date = iso;
        if (lvl === 'B1') result.b1Date = iso;
        if (lvl === 'B2') result.b2Date = iso;
        if (lvl === 'C1') result.c1Date = iso;
        if (lvl === 'C2') result.c2Date = iso;

        accumulatedHours = requiredHours;
      }
    });

    return {
      a2Date: result.a2Date,
      b1Date: result.b1Date,
      b2Date: result.b2Date,
      c1Date: result.c1Date,
      c2Date: result.c2Date
    };
  }

  /**
   * Salva ou atualiza um plano de estudos tanto localmente quanto no Cloudflare D1
   */
  public async savePlan(plan: StudyPlan): Promise<void> {
    const cleanLang = plan.language_code.toLowerCase().split('-')[0];
    const key = `${this.LOCAL_STORAGE_KEY_PREFIX}${plan.user_id}_${cleanLang}`;

    // 1. Salvar no LocalStorage (Offline-First imediato)
    try {
      localStorage.setItem(key, JSON.stringify(plan));
      localStorage.setItem('libritui_active_study_lang', cleanLang);
    } catch (e) {
      console.warn('[studyPlanService] Falha ao salvar no localStorage:', e);
    }

    // 2. Persistir no D1 se o usuário não for guest
    if (plan.user_id && plan.user_id !== 'guest') {
      try {
        await dbManager.from('user_study_plans', plan.user_id).upsert({
          id: plan.id || `plan_${plan.user_id}_${cleanLang}`,
          user_id: plan.user_id,
          language_code: cleanLang,
          current_level: plan.current_level,
          daily_goal_minutes: plan.daily_goal_minutes,
          current_day_index: plan.current_day_index || 1,
          start_date: plan.start_date || getLocalISOString().split('T')[0],
          projected_a2_date: plan.projected_a2_date || null,
          projected_b1_date: plan.projected_b1_date || null,
          projected_b2_date: plan.projected_b2_date || null,
          projected_c1_date: plan.projected_c1_date || null,
          projected_c2_date: plan.projected_c2_date || null,
          total_minutes_studied: plan.total_minutes_studied || 0,
          updated_at: new Date().toISOString()
        });
      } catch (err) {
        console.warn('[studyPlanService] Falha ao sincronizar plano no D1 (salvo local):', err);
      }
    }
  }

  /**
   * Obtém todos os planos de estudos do usuário cadastrados no LocalStorage e no D1
   */
  public async getUserPlans(userId: string | undefined): Promise<StudyPlan[]> {
    const plansMap = new Map<string, StudyPlan>();

    // 1. LocalStorage
    try {
      const prefix = `${this.LOCAL_STORAGE_KEY_PREFIX}${userId || 'guest'}_`;
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(prefix) && !k.includes('_day_')) {
          try {
            const val = localStorage.getItem(k);
            if (val) {
              const p = JSON.parse(val) as StudyPlan;
              if (p && p.language_code) {
                const cleanLang = p.language_code.toLowerCase().split('-')[0];
                plansMap.set(cleanLang, p);
              }
            }
          } catch {}
        }
      }
    } catch (e) {}

    // 2. D1 (apenas se usuário logado)
    if (userId && userId !== 'guest') {
      try {
        const res: any = await dbManager.from('user_study_plans', userId).select();
        const rows = Array.isArray(res) ? res : (res?.data || res?.results || []);
        rows.forEach((r: any) => {
          const cleanLang = (r.language_code || 'en').toLowerCase().split('-')[0];
          if (!plansMap.has(cleanLang)) {
            plansMap.set(cleanLang, {
              id: r.id,
              user_id: r.user_id,
              language_code: r.language_code,
              current_level: r.current_level,
              daily_goal_minutes: r.daily_goal_minutes,
              current_day_index: r.current_day_index || 1,
              start_date: r.start_date,
              projected_a2_date: r.projected_a2_date,
              projected_b1_date: r.projected_b1_date,
              projected_b2_date: r.projected_b2_date,
              projected_c1_date: r.projected_c1_date,
              projected_c2_date: r.projected_c2_date,
              total_minutes_studied: r.total_minutes_studied || 0
            });
          }
        });
      } catch (e) {
        console.warn('[studyPlanService] Erro ao carregar planos do D1:', e);
      }
    }

    return Array.from(plansMap.values());
  }

  /**
   * Obtém o plano de estudos ativo (Local -> D1 fallback)
   */
  public async getPlan(userId: string | undefined, langCode: string): Promise<StudyPlan | null> {
    const cleanLang = (langCode || 'en').toLowerCase().split('-')[0];
    const key = `${this.LOCAL_STORAGE_KEY_PREFIX}${userId || 'guest'}_${cleanLang}`;

    // 1. Tentar ler do LocalStorage
    try {
      const cached = localStorage.getItem(key);
      if (cached) {
        return JSON.parse(cached) as StudyPlan;
      }
    } catch (e) {}

    // 2. Tentar buscar no D1
    if (userId && userId !== 'guest') {
      try {
        const res: any = await dbManager.from('user_study_plans', userId).select({ language_code: cleanLang });
        const row = Array.isArray(res) ? res[0] : (res?.data?.[0] || res?.results?.[0]);

        if (row) {
          const plan: StudyPlan = {
            id: row.id,
            user_id: row.user_id,
            language_code: row.language_code,
            current_level: row.current_level,
            daily_goal_minutes: row.daily_goal_minutes,
            current_day_index: row.current_day_index || 1,
            start_date: row.start_date,
            projected_a2_date: row.projected_a2_date,
            projected_b1_date: row.projected_b1_date,
            projected_b2_date: row.projected_b2_date,
            projected_c1_date: row.projected_c1_date,
            projected_c2_date: row.projected_c2_date,
            total_minutes_studied: row.total_minutes_studied || 0
          };
          localStorage.setItem(key, JSON.stringify(plan));
          return plan;
        }
      } catch (err) {
        console.warn('[studyPlanService] Erro ao carregar plano do D1:', err);
      }
    }

    return null;
  }

  /**
   * Exclui o plano de estudos e todo o histórico de progresso diário de um determinado idioma
   * no LocalStorage e no Cloudflare D1.
   */
  public async deletePlan(userId: string | undefined, langCode: string): Promise<boolean> {
    const cleanLang = (langCode || 'en').toLowerCase().split('-')[0];
    const userKey = userId || 'guest';

    // 1. Limpar LocalStorage de chaves do plano, tarefas e contadores do idioma
    try {
      const planKey = `${this.LOCAL_STORAGE_KEY_PREFIX}${userKey}_${cleanLang}`;
      localStorage.removeItem(planKey);

      // Limpar tarefas diárias salvas em cache local
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k) {
          if (k.startsWith(`${this.LOCAL_TASKS_KEY_PREFIX}${userKey}_${cleanLang}_`)) {
            keysToRemove.push(k);
          } else if (k.startsWith(`libritui_task_items_${userKey}_${cleanLang}_`)) {
            keysToRemove.push(k);
          } else if (k.startsWith(`libritui_task_time_${userKey}_${cleanLang}_`)) {
            keysToRemove.push(k);
          }
        }
      }
      keysToRemove.forEach(k => localStorage.removeItem(k));
    } catch (e) {
      console.warn('[studyPlanService] Falha ao limpar LocalStorage ao excluir plano:', e);
    }

    // 2. Excluir no Cloudflare D1 se não for guest
    if (userId && userId !== 'guest') {
      try {
        await Promise.allSettled([
          dbManager.from('user_study_plans', userId).deleteMatch({ language_code: cleanLang }),
          dbManager.from('user_daily_plan_progress', userId).deleteMatch({ language_code: cleanLang })
        ]);
      } catch (err) {
        console.warn('[studyPlanService] Falha ao deletar plano no D1:', err);
      }
    }

    return true;
  }

  /**
   * Gera as tarefas curriculares de um determinado dia do plano.
   * Abrange: Flashcards (palavras e frases), Pronúncia, Listening, Escrita,
   * Gramática (Leitura e Quiz), e Audiobooks Sincronizados (apenas para Inglês).
   */
  public generateTasksForDay(params: {
    dayIndex: number;
    level: string;
    langCode: string;
    dailyMinutes: number;
    pendingErrors?: any[];
  }): PlanTask[] {
    const { dayIndex, level, langCode, dailyMinutes: targetDailyMinutes, pendingErrors = [] } = params;
    const cleanLang = (langCode || 'en').toLowerCase().split('-')[0];
    const lvlUpper = (level || 'A1').toUpperCase();
    const dailyMinutes = Math.max(10, targetDailyMinutes || 30);
    const tasks: PlanTask[] = [];

    // Pesos relativos de importância pedagógica
    const WEIGHTS: Record<string, number> = {
      review_errors: 15,
      flashcards_words: 20,
      flashcards_phrases: 20,
      pronunciation: 25,
      listening: 20,
      grammar_reading: 15,
      grammar_quiz: 15,
      writing: 15,
      synced_audiobook: 15
    };

    // 1. TAREFAS PRIORITÁRIAS DE ERROS (Se houver erros pendentes de dias anteriores)
    if (pendingErrors.length > 0) {
      const errorCount = Math.min(pendingErrors.length, 5);
      tasks.push({
        id: `task_errors_${dayIndex}`,
        type: 'review_errors',
        title: `⚠️ Refazer Erros Pendentes (${errorCount} itens)`,
        description: 'Revise os exercícios que você errou anteriormente até atingir a maestria.',
        estimated_minutes: 0,
        target_count: errorCount,
        level: lvlUpper,
        target_url: `/review-errors/guide?lang=${cleanLang}&from=guide`,
        completed: false,
        is_priority_error: true,
        metadata: { pending_count: errorCount }
      });
    }

    // 2. GRADE DE DISTRIBUIÇÃO CONFORME A FASE / DIA
    const isOddDay = dayIndex % 2 !== 0;

    // Flashcards (Palavras em dias ímpares, Frases em dias pares)
    if (isOddDay) {
      tasks.push({
        id: `task_fc_words_${dayIndex}`,
        type: 'flashcards_words',
        title: `Vocabulário: 15 Palavras (${lvlUpper})`,
        description: 'Aprenda e retenha novas palavras com a repetição espaçada e traduções detalhadas.',
        estimated_minutes: 0,
        target_count: 15,
        level: lvlUpper,
        target_url: `/flashcards/guide/${cleanLang}/${lvlUpper.toLowerCase()}?mode=words&deck=words_${lvlUpper}&from=guide`,
        completed: false
      });
    } else {
      tasks.push({
        id: `task_fc_phrases_${dayIndex}`,
        type: 'flashcards_phrases',
        title: `Expressões & Frases Úteis (${lvlUpper})`,
        description: 'Fixe estruturas em contexto real com cartões de frases práticas.',
        estimated_minutes: 0,
        target_count: 15,
        level: lvlUpper,
        target_url: `/flashcards/guide/${cleanLang}/${lvlUpper.toLowerCase()}?mode=phrases&from=guide`,
        completed: false
      });
    }

    // Pronúncia Fonética (húngaro ainda não tem exercícios de pronúncia no R2)
    if (cleanLang !== 'hu') tasks.push({
      id: `task_pronunciation_${dayIndex}`,
      type: 'pronunciation',
      title: `Pronúncia Fonética (${lvlUpper})`,
      description: 'Treine a fala com áudios de nativos do R2 e feedback de espectrograma em tempo real.',
      estimated_minutes: 0,
      target_seconds: 0,
      level: lvlUpper,
      target_url: `/pronunciation/guide/${cleanLang}/${lvlUpper.toLowerCase()}?from=guide`,
      completed: false
    });

    // Compreensão Auditiva (Listening)
    tasks.push({
      id: `task_listening_${dayIndex}`,
      type: 'listening',
      title: `Compreensão Auditiva (${lvlUpper})`,
      description: 'Escute com atenção e monte as frases em blocos de palavras com precisão fonética.',
      estimated_minutes: 0,
      target_seconds: 0,
      level: lvlUpper,
      target_url: `/listening/guide/${cleanLang}/${lvlUpper.toLowerCase()}?from=guide`,
      completed: false
    });

    // A partir do Dia 3: Introduzir Gramática e Quizzes
    if (dayIndex >= 3) {
      const langGrammar = getGrammarTopicsForLangAndLevel(cleanLang, lvlUpper.toLowerCase());
      const topicIndex = (dayIndex - 3) % langGrammar.length;
      const topic = langGrammar[topicIndex] || langGrammar[0];
      const topicName = topic.label;

      if (dayIndex % 3 === 0) {
        tasks.push({
          id: `task_grammar_read_${dayIndex}`,
          type: 'grammar_reading',
          title: `Gramática: ${topicName}`,
          description: `Leia as regras e exemplos práticos da aula de ${topicName}.`,
          estimated_minutes: 0,
          target_seconds: 0,
          level: lvlUpper,
          target_url: `/grammar/guide/${cleanLang}/${lvlUpper.toLowerCase()}/${topic.id}?view=explanation&from=guide`,
          completed: false,
          metadata: { topicId: topic.id, topicName: topicName }
        });
      } else {
        tasks.push({
          id: `task_grammar_quiz_${dayIndex}`,
          type: 'grammar_quiz',
          title: `Quiz de Fixação: ${topicName}`,
          description: `Teste seu entendimento sobre ${topicName} com questões interativas.`,
          estimated_minutes: 0,
          target_count: 5,
          level: lvlUpper,
          target_url: `/grammar/guide/${cleanLang}/${lvlUpper.toLowerCase()}/${topic.id}?view=quiz&from=guide`,
          completed: false,
          metadata: { topicId: topic.id, topicName: topicName }
        });
      }
    }

    // Prática de Escrita (Dias pares a partir do dia 2 ou qualquer dia a partir do dia 5)
    if (!isOddDay || dayIndex >= 5) {
      tasks.push({
        id: `task_writing_${dayIndex}`,
        type: 'writing',
        title: `Prática de Escrita (${lvlUpper})`,
        description: 'Escreva frases completas exercitando estrutura sintática e ortografia.',
        estimated_minutes: 0,
        target_count: 5,
        level: lvlUpper,
        target_url: `/writing/guide/${cleanLang}/${lvlUpper.toLowerCase()}?from=guide`,
        completed: false
      });
    }

    // AUDIOBOOKS SINCRONIZADOS (SOMENTE PARA INGLÊS, DIAS PARES OU SE TEMPO >= 15 MIN)
    if (cleanLang === 'en' && dailyMinutes >= 15 && dayIndex % 2 === 0) {
      tasks.push({
        id: `task_synced_audiobook_${dayIndex}`,
        type: 'synced_audiobook',
        title: `Audiobook Sincronizado`,
        description: `Escute e acompanhe o texto sincronizado em inglês.`,
        estimated_minutes: 0,
        target_seconds: 0,
        level: lvlUpper,
        target_url: `/audiobooks?lang=English`,
        completed: false,
        metadata: { lang: 'English' }
      });
    }

    // 3. NORMALIZAÇÃO MATEMÁTICA: A SOMA DAS TAREFAS DEVE SER EXATAMENTE = dailyMinutes
    const totalWeight = tasks.reduce((sum, t) => sum + (WEIGHTS[t.type] || 20), 0);
    let allocatedMinutes = 0;
    const taskAllocations = tasks.map(t => {
      const weight = WEIGHTS[t.type] || 20;
      const exactShare = (dailyMinutes * weight) / totalWeight;
      const mins = Math.max(3, Math.floor(exactShare));
      const remainderScore = exactShare - Math.floor(exactShare);
      allocatedMinutes += mins;
      return { task: t, mins, remainderScore, weight };
    });

    let diff = dailyMinutes - allocatedMinutes;
    if (diff > 0) {
      taskAllocations.sort((a, b) => b.remainderScore - a.remainderScore || b.weight - a.weight);
      for (let i = 0; i < diff; i++) {
        taskAllocations[i % taskAllocations.length].mins += 1;
      }
    } else if (diff < 0) {
      while (diff < 0) {
        taskAllocations.sort((a, b) => b.mins - a.mins);
        if (taskAllocations[0].mins > 3) {
          taskAllocations[0].mins -= 1;
          diff += 1;
        } else {
          break;
        }
      }
    }

    // 4. ATRIBUIR METAS FINAIS E AJUSTAR TÍTULOS
    taskAllocations.forEach(({ task, mins }) => {
      task.estimated_minutes = mins;

      if (task.type === 'pronunciation' || task.type === 'listening' || task.type === 'grammar_reading' || task.type === 'synced_audiobook') {
        task.target_seconds = mins * 60;
        if (task.type === 'synced_audiobook') {
          task.title = `Audiobook Sincronizado (${mins} min)`;
          task.description = `Escute e acompanhe o texto sincronizado em inglês por ${mins} minutos.`;
          task.target_url = `/audiobooks?lang=English&targetTime=${mins}`;
          if (task.metadata) task.metadata.target_minutes = mins;
        }
      }

      if (task.type === 'flashcards_words' || task.type === 'flashcards_phrases') {
        let count = 15;
        if (mins <= 6) count = 10;
        else if (mins <= 14) count = 15;
        else if (mins <= 20) count = 20;
        else if (mins <= 28) count = 25;
        else count = 30;

        task.target_count = count;
        if (task.type === 'flashcards_words') {
          task.title = `Vocabulário: ${count} Palavras (${lvlUpper})`;
        } else {
          task.title = `Expressões & Frases Úteis (${count} itens)`;
        }
      }

      if (task.type === 'writing' || task.type === 'grammar_quiz') {
        let count = 5;
        if (mins >= 15) count = 10;
        else if (mins >= 10) count = 8;
        task.target_count = count;
      }
    });

    return tasks;
  }

  /**
   * Salva o progresso diário de tarefas no LocalStorage e no D1
   */
  public async saveDailyProgress(userId: string | undefined, langCode: string, progress: DailyPlanProgress): Promise<void> {
    const cleanLang = (langCode || 'en').toLowerCase().split('-')[0];
    const key = `${this.LOCAL_TASKS_KEY_PREFIX}${userId || 'guest'}_${cleanLang}_${progress.plan_day}`;

    // 1. Salvar no LocalStorage
    try {
      localStorage.setItem(key, JSON.stringify(progress));
    } catch (e) {}

    // 2. Persistir no Cloudflare D1
    if (userId && userId !== 'guest') {
      try {
        await dbManager.from('user_daily_plan_progress', userId).upsert({
          id: `prog_${userId}_${cleanLang}_d${progress.plan_day}`,
          user_id: userId,
          language_code: cleanLang,
          plan_day: progress.plan_day,
          schedule_date: progress.schedule_date,
          tasks_json: JSON.stringify(progress.tasks),
          completed_tasks_count: progress.completed_tasks_count,
          total_tasks_count: progress.total_tasks_count,
          minutes_spent: progress.minutes_spent,
          status: progress.status,
          completed_at: progress.status === 'completed' ? new Date().toISOString() : null,
          updated_at: new Date().toISOString()
        });
      } catch (err) {
        console.warn('[studyPlanService] Falha ao sincronizar progresso no D1:', err);
      }
    }
  }

  /**
   * Carrega o progresso de tarefas do dia atual
   */
  public async getDailyProgress(userId: string | undefined, langCode: string, planDay: number): Promise<DailyPlanProgress | null> {
    const cleanLang = (langCode || 'en').toLowerCase().split('-')[0];
    const key = `${this.LOCAL_TASKS_KEY_PREFIX}${userId || 'guest'}_${cleanLang}_${planDay}`;

    // 1. LocalStorage
    try {
      const cached = localStorage.getItem(key);
      if (cached) {
        return JSON.parse(cached) as DailyPlanProgress;
      }
    } catch (e) {}

    // 2. D1
    if (userId && userId !== 'guest') {
      try {
        const res: any = await dbManager.from('user_daily_plan_progress', userId).select({
          language_code: cleanLang,
          plan_day: planDay
        });
        const row = Array.isArray(res) ? res[0] : (res?.data?.[0] || res?.results?.[0]);

        if (row) {
          const loaded: DailyPlanProgress = {
            plan_day: row.plan_day,
            schedule_date: row.schedule_date,
            tasks: typeof row.tasks_json === 'string' ? JSON.parse(row.tasks_json) : (row.tasks_json || []),
            completed_tasks_count: row.completed_tasks_count || 0,
            total_tasks_count: row.total_tasks_count || 0,
            minutes_spent: row.minutes_spent || 0,
            status: row.status || 'pending'
          };
          localStorage.setItem(key, JSON.stringify(loaded));
          return loaded;
        }
      } catch (err) {}
    }

    return null;
  }

  /**
   * Marca uma tarefa como concluída e verifica se o dia foi finalizado
   */
  public async toggleTaskComplete(params: {
    userId: string | undefined;
    langCode: string;
    planDay: number;
    taskId: string;
    completed: boolean;
  }): Promise<DailyPlanProgress | null> {
    const { userId, langCode, planDay, taskId, completed } = params;
    const progress = await this.getDailyProgress(userId, langCode, planDay);
    if (!progress) return null;

    const taskIndex = progress.tasks.findIndex(t => t.id === taskId);
    if (taskIndex >= 0) {
      const task = progress.tasks[taskIndex];
      task.completed = completed;
      if (completed) {
        task.current_progress = task.target_count || task.target_seconds || (task.estimated_minutes * 60);
      }
      // Sincronizar chaves individuais no localStorage desacopladas de data
      try {
        const cleanL = (langCode || 'en').toLowerCase().split('-')[0];
        const keyItems = `libritui_task_items_${userId || 'guest'}_${cleanL}_${planDay}_${task.id}`;
        const keyTime = `libritui_task_time_${userId || 'guest'}_${cleanL}_${planDay}_${task.id}`;
        if (completed) {
          if (task.target_count) localStorage.setItem(keyItems, String(task.target_count));
          const tSec = task.target_seconds || (task.estimated_minutes * 60);
          if (tSec) localStorage.setItem(keyTime, String(tSec));
        }
      } catch (e) {}
    }

    const completedCount = progress.tasks.filter(t => t.completed).length;
    progress.completed_tasks_count = completedCount;
    progress.total_tasks_count = progress.tasks.length;
    progress.status = completedCount >= progress.total_tasks_count ? 'completed' : completedCount > 0 ? 'in_progress' : 'pending';

    await this.saveDailyProgress(userId, langCode, progress);
    return progress;
  }

  /**
   * Atualiza o progresso parcial de uma tarefa específica (ex: 5 de 15 flashcards ou 75 segundos)
   */
  public async updateTaskCurrentProgress(params: {
    userId: string | undefined;
    langCode: string;
    planDay: number;
    taskId: string;
    currentProgress: number;
    autoCompleteIfTargetReached?: boolean;
  }): Promise<DailyPlanProgress | null> {
    const { userId, langCode, planDay, taskId, currentProgress, autoCompleteIfTargetReached = true } = params;
    const progress = await this.getDailyProgress(userId, langCode, planDay);
    if (!progress) return null;

    const taskIndex = progress.tasks.findIndex(t => t.id === taskId);
    if (taskIndex >= 0) {
      const task = progress.tasks[taskIndex];
      task.current_progress = Math.max(task.current_progress || 0, currentProgress);

      if (autoCompleteIfTargetReached) {
        if (task.target_count && task.current_progress >= task.target_count) {
          task.completed = true;
        } else if (task.target_seconds && task.current_progress >= task.target_seconds) {
          task.completed = true;
        }
      }

      // Sincronizar chaves individuais no localStorage desacopladas de data
      try {
        const cleanL = (langCode || 'en').toLowerCase().split('-')[0];
        const keyItems = `libritui_task_items_${userId || 'guest'}_${cleanL}_${planDay}_${task.id}`;
        const keyTime = `libritui_task_time_${userId || 'guest'}_${cleanL}_${planDay}_${task.id}`;
        if (task.target_count) localStorage.setItem(keyItems, String(task.current_progress));
        if (task.target_seconds || task.estimated_minutes) localStorage.setItem(keyTime, String(task.current_progress));
      } catch (e) {}
    }

    const completedCount = progress.tasks.filter(t => t.completed).length;
    progress.completed_tasks_count = completedCount;
    progress.total_tasks_count = progress.tasks.length;
    progress.status = completedCount >= progress.total_tasks_count ? 'completed' : completedCount > 0 ? 'in_progress' : 'pending';

    await this.saveDailyProgress(userId, langCode, progress);
    return progress;
  }
}

export const studyPlanService = new StudyPlanService();
